import _ from "lodash";
import React, { useState, useEffect, useRef, forwardRef } from "react";
import { useMount } from "react-use";
import classes from "./SearchInput.scss";
import {
  CloseOutlined,
  MicrophoneOutlined,
  SearchOutlined,
} from "@toddle-edu/ds-icons";
import { useDebouncedCallback } from "Utils/CustomHooks";
import useSpeechToText from "react-hook-speech-to-text";
import {
  ALL_CURRICULUM_INDICATOR,
  getIsGlobalSearchJumpToV2Enabled,
  getIsAllCurriculumSelected,
  typesConfig,
} from "../Utils";
import { withCombinedAccessBlocker } from "lib/PermissionAware";
import { isChrome } from "Utils/DeviceDetectHelpers";
import I18nHOC from "UIComponents/I18nHOC";
import classNames from "classnames";
import ACLStore from "lib/aclStore";
import { ButtonDisclosure, Dropdown, DropdownMenu } from "@toddle-edu/ds-web";
import { recordEvent } from "lib/eventTracker";

const KeyNameComp = props => {
  const { label } = props;
  return (
    <span className={classNames("text-body-3", classes.keyNameContainer)}>
      {label}
    </span>
  );
};

const MicComponent = props => {
  const {
    enableVoiceSearch,
    setSearchText,
    toggleVoiceSearch,
    delayedChange,
  } = props;

  const {
    isRecording,
    interimResult,
    results,
    startSpeechToText,
    stopSpeechToText,
  } = useSpeechToText({
    continuous: true,
    useLegacyResults: false,
  });

  const intervalRef = useRef(null);

  //stop voice search after 1 seconds of last return results

  useEffect(() => {
    let count = 0;
    if (isRecording) {
      intervalRef.current = setInterval(() => {
        const currentTime = Math.floor(new Date().getTime() / 1000);

        //increase count each time interval run

        count++;

        //if there is time stamp in result will get that
        //otherwise timestamp for that transcript
        const timeDiff =
          currentTime - _.get(_.last(results), "timestamp", currentTime);

        //if result is empty that means timeDiff will be 0 and
        //else part will be checked otherwise first part will be checked
        if (timeDiff && timeDiff > 1000) {
          clearInterval(intervalRef.current);
          stopSpeechToText();
          toggleVoiceSearch({ state: false });

          //if total count is 3 that means total 3 time this was run -- 3 seconds so
          //stop the speech recognition
        } else if (count === 3) {
          clearInterval(intervalRef.current);
          stopSpeechToText();
          toggleVoiceSearch({ state: false });
        }
      }, 1000);
    }
  }, [isRecording]);

  //this will run for mount and de-mount of component
  //and check whether should enable voice search on mount on component or not
  useEffect(() => {
    if (enableVoiceSearch) {
      setSearchText("");
      startSpeechToText();
    }

    return () => {
      clearInterval(intervalRef?.current);
      delayedChange.cancel();
      stopSpeechToText();
    };
  }, [enableVoiceSearch]);

  //this will update text on search bar
  useEffect(() => {
    if (interimResult) {
      setSearchText(interimResult);
    }
  }, [interimResult]);

  const onMicroPhoneClick = () => {
    if (isRecording) {
      clearInterval(intervalRef?.current);
      stopSpeechToText();
      toggleVoiceSearch({ state: false });
    } else {
      toggleVoiceSearch();
    }
  };

  return (
    <MicrophoneOutlined
      onClick={onMicroPhoneClick}
      variant={isRecording ? "link" : "subtle"}
    />
  );
};

const MicComponentWrapper = withCombinedAccessBlocker(MicComponent);

const getCurriculumDropdownOptions = ({ curriculumPrograms, t }) => {
  const curriculumProgramOptions = _.map(curriculumPrograms, program => {
    return {
      key: program.id,
      label: program.acronym,
    };
  });

  if (_.size(curriculumProgramOptions) > 1) {
    curriculumProgramOptions.push({
      key: "all",
      label: t("common:all"),
    });
  }
  return curriculumProgramOptions;
};

export const SearchInput = forwardRef((props, inputRef) => {
  const {
    searchText = "",
    onChange = () => {},
    module,
    onUnselectModule,
    enableVoiceSearch,
    toggleVoiceSearch,
    t,
    selectedCurriculum,
    curriculumPrograms,
    onCurriculumChange,
    curriculumDropdownRef,
    platformCurriculumProgramId = null,
  } = props;
  const [value, setValue] = useState({ searchText, initValue: searchText });

  /**
   * if open modal there is some value by default on backspace both chip and text should be
   * removed
   *
   * this state is for that if search text value and initial value is same then on press
   * remove chip
   *
   * other wise on every text change setInitialValue to empty or null
   */

  const [delayedChange] = useDebouncedCallback(onChange, 300, [
    module,
    searchText,
  ]);

  const setInputValue = value => {
    delayedChange(value);
    setValue({ initValue: "", searchText: value });
  };

  const onCrossClick = () => {
    setInputValue("");
  };

  const onInputChange = e => {
    setInputValue(e.target.value);
  };

  const setInitialValue = params => {
    setValue({ ...value, initValue: params });
  };

  useMount(() => {
    if (searchText) {
      inputRef?.current?.select();
    }

    if (module && !value.searchText) {
      onUnselectModule();
    }
  });

  const onKeyDown = e => {
    if (
      e.key === "Tab" &&
      curriculumDropdownRef.current &&
      !isGlobalSearchJumpToV2Enabled
    ) {
      e.preventDefault();
      curriculumDropdownRef.current.focus();
    }

    /**
     * for de-selecting selected text either user press mouse or left or right arrow key
     * so in those case we set initial value to empty
     */
    if (["ArrowLeft", "ArrowRight"].includes(e.key)) {
      setInitialValue("");
    }

    /**
     * @description disable cursor position
     * jump to stat/end of line
     */
    if (["ArrowUp", "ArrowDown"].includes(e.key)) {
      e.preventDefault();
    }
  };

  const onMouseDown = () => {
    setInitialValue("");
  };

  const renderKeyPairComp = () => {
    return (
      <div className={classes.keyPairContainer}>
        <KeyNameComp label="/" />
      </div>
    );
  };

  const isGlobalSearchJumpToV2Enabled = getIsGlobalSearchJumpToV2Enabled();

  const onCurriculumProgramChange = ({ key }) => {
    recordEvent({
      eventName: "GLOBAL_SEARCH",
      eventData: {
        event_type: "CURRICULUM_SWITCH",
        targetCurriculumId: key,
        platformCurriculumProgramId,
      },
    });

    if (key === "all") {
      return onCurriculumChange({
        id: ALL_CURRICULUM_INDICATOR,
      });
    }
    const selectedProgram = _.find(curriculumPrograms, { id: key });
    onCurriculumChange(selectedProgram);
  };

  //this is permission to show mic on global search bar
  const perm = "FeatureFlag:ShowMicOption";

  const showVoiceText = ACLStore.can(perm) && isChrome();

  const searchInputStyle = classNames("body-l", classes.searchInput, {
    [classes.placeHolderColor]: enableVoiceSearch,
  });

  const isAllCurriculumSelected = getIsAllCurriculumSelected(
    selectedCurriculum?.id
  );

  const dropdownLabel = isAllCurriculumSelected
    ? t("common:all")
    : _.get(selectedCurriculum, "acronym", "");

  const currentCurriculumProgramLabel = _.get(
    selectedCurriculum,
    "label",
    null
  );

  const getSearchInputPlaceholder = () => {
    if (enableVoiceSearch) {
      return t("integration:listening");
    }

    if (isAllCurriculumSelected) {
      return module
        ? t(
            "integration:global_search_placeholder_with_module_and_all_curriculum",
            {
              module: t(typesConfig[module].title),
            }
          )
        : t("integration:global_search_placeholder_with_all_curriculum");
    }

    if (currentCurriculumProgramLabel) {
      return module
        ? t(
            "integration:global_search_placeholder_with_module_and_curriculum",
            {
              module: t(typesConfig[module].title),
              curriculum: currentCurriculumProgramLabel,
            }
          )
        : `${t("common:search_within_label", {
            label: currentCurriculumProgramLabel,
          })}...`;
    }

    return `${t("common:search")}...`;
  };

  return (
    <div className={classes.searchBar}>
      <div className={classes.searchContainer}>
        <SearchOutlined className={classes.searchIcon} />
        <div className={classes.searchInputContainer}>
          <input
            id="global-search-input"
            ref={inputRef}
            type="text"
            className={searchInputStyle}
            placeholder={getSearchInputPlaceholder()}
            value={value.searchText}
            onChange={onInputChange}
            onKeyDown={onKeyDown}
            onMouseDown={onMouseDown}
            autoComplete="off"
            data-test-id="globalSearch-searchInput-input"
          />
        </div>
        {value.searchText && (
          <div className={classes.clearIcon}>
            <CloseOutlined
              onClick={onCrossClick}
              variant="subtle"
              data-test-id="globalSearch-searchInput-clearInputButton"
            />
          </div>
        )}
        {!isGlobalSearchJumpToV2Enabled ? renderKeyPairComp() : null}
        {value.searchText && showVoiceText && (
          <div className={classes.border}></div>
        )}
        {isChrome() ? (
          <div
            className={classes.microPhone}
            role="button"
            aria-pressed="false"
            aria-label={t("a11y:voice_search")}
          >
            <MicComponentWrapper
              enableVoiceSearch={enableVoiceSearch}
              setSearchText={setInputValue}
              toggleVoiceSearch={toggleVoiceSearch}
              accessPermissions={perm}
              showErrorComp={false}
              delayedChange={delayedChange}
            />
          </div>
        ) : null}
      </div>
      {!_.isEmpty(curriculumPrograms) ? (
        <div className={classes.dropdownContainer}>
          <Dropdown
            showSelection
            overlay={
              <DropdownMenu
                dsVersion="2.0"
                testId="globalSearch-searchInput-curriculumDropdown"
                size="medium"
                options={getCurriculumDropdownOptions({
                  curriculumPrograms,
                  t,
                })}
                onClick={onCurriculumProgramChange}
                shouldStopPropagation
              />
            }
          >
            <ButtonDisclosure
              className={classes.dropdownLabel}
              dsVersion="2.0"
              testId="globalSearch-searchInput-curriculumDropdown"
              size={"medium"}
              type={"plain"}
              variant={"neutral"}
              ref={curriculumDropdownRef}
              isFullWidth={true}
            >
              <div className={"text-label-2"}>{dropdownLabel}</div>
            </ButtonDisclosure>
          </Dropdown>
        </div>
      ) : null}
    </div>
  );
});

SearchInput.displayName = "SearchInput";

export default I18nHOC({ resource: ["common", "teacherHomePage", "a11y"] })(
  SearchInput
);
